import { useEffect, useRef } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "react-toastify";
import { onNewAwards } from "../../utils/awardEvents";
import { ACHIEVEMENTS_KEY } from "../../utils/cacheUtils";
import { burstFrom } from "../../utils/confetti";
import AchievementBadge from "../../components/AchievementBadge";
import { getAchievementIcon } from "./AchievementRow";

const BADGE = 40; // rosette size (px)
// Where AchievementBadge's disc sits in its square (centre 20,16 of 40, ~14 radius)
const DISC_BOX = { left: "15%", top: "5%", width: "70%", height: "70%" };
// Long enough to take in what's been unlocked before the burst; the icon's
// ripple fires at the same moment, so the celebration lands as one beat
const CONFETTI_DELAY_MS = 1000;

/**
 * The unlock toast's content: the badge (AchievementBadge) pops in, then a
 * ripple and a confetti burst from its disc, beside what was earned.
 */
function UnlockToast({ award }) {
  const reduceMotion = useReducedMotion();
  const theme = useTheme();
  const icon = getAchievementIcon(award.icon);
  const badgeRef = useRef(null);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const timer = setTimeout(() => {
      if (badgeRef.current) {
        burstFrom(badgeRef.current, [
          theme.palette.primary.main,
          theme.palette.tertiary.main,
          "#ffffff",
        ]);
      }
    }, CONFETTI_DELAY_MS);
    return () => clearTimeout(timer);
  }, [reduceMotion, theme]);

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          position: "relative",
          width: BADGE,
          height: BADGE,
          flexShrink: 0,
        }}
      >
        <Box
          component={motion.div}
          initial={reduceMotion ? false : { scale: 0, rotate: -30 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: "spring",
            stiffness: 480,
            damping: 16,
            delay: 0.1,
          }}
        >
          <AchievementBadge icon={icon} size={BADGE} />
        </Box>
        {/* Sits over the rosette's disc (not the whole square, which
            includes the tails): the ripple and the confetti centre on it */}
        <Box
          ref={badgeRef}
          sx={{
            position: "absolute",
            ...DISC_BOX,
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        >
          {!reduceMotion && (
            <Box
              component={motion.div}
              initial={{ scale: 1, opacity: 0 }}
              animate={{ scale: [1, 1.9], opacity: [0.6, 0] }}
              transition={{ delay: CONFETTI_DELAY_MS / 1000, duration: 0.7 }}
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                border: 2,
                borderColor: "primary.main",
              }}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight="bold" lineHeight={1.3}>
          {award.title}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.75 }}>
          {award.kind === "checklist"
            ? "Complete"
            : `Level ${award.level} unlocked`}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * Celebrates achievements unlocked by any write, wherever it happened: the
 * API layer reads the server's new-awards header and hands them here (see
 * utils/awardEvents.js). One unlock toast each, up for 5s, a haptic tap where the device
 * supports it (not iOS Safari), and the dashboard's rows refreshed. Doesn't
 * touch the NEW dots — those clear only once seen on the dashboard.
 * Mounted once, in RootLayout; renders nothing itself.
 */
export default function AwardCelebrations() {
  const queryClient = useQueryClient();

  useEffect(
    () =>
      onNewAwards((awards) => {
        awards.forEach((award) =>
          // Twice the usual toast's time: the confetti fires at 1s, and there
          // should be time to take it in afterwards
          toast(<UnlockToast award={award} />, {
            icon: false,
            autoClose: 5000,
          }),
        );
        try {
          navigator.vibrate?.([20, 40, 30]);
        } catch {
          // Unsupported, or blocked until the user has interacted — skip it
        }
        queryClient.invalidateQueries({ queryKey: ACHIEVEMENTS_KEY });
      }),
    [queryClient],
  );

  return null;
}
